import {
  useWSModal,
  WSActions,
  WSAlert,
  WSInfoBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { BulkResource } from "../../types";
import { useGoToContractorBatches, useGoToPayableBatches } from "../../paths";
import { getBulkQueriesByResource } from "../../query/bulkBatchResourceQueryMap";
import { IBulkBatch } from "@wingspanhq/payments/dist/interfaces";
import useSnackbar from "../../../../hooks/useSnackbar";
import { getUploadedFilename } from "../../utils/getUploadedFilename";
import { useFlatfileUploadJobStatus } from "../../hooks/useFlatfileUploadJobStatus";

const ModalConfirmBulkBatchDelete: React.FC<{
  onClose: () => void;
  bulkBatchId: string;
  isEmptyBatch: boolean;
  bulkResource: BulkResource;
}> = ({ onClose, bulkBatchId, isEmptyBatch, bulkResource }) => {
  const { success, warning } = useSnackbar();
  const { useBulkBatch, useDeleteBulkBatch } =
    getBulkQueriesByResource(bulkResource);
  const queryBulkBatch = useBulkBatch(bulkBatchId);
  const [deleteBulkBatch, deleteBulkBatchMeta] = useDeleteBulkBatch();
  const goToContractorBatches = useGoToContractorBatches();
  const goToPayableBatches = useGoToPayableBatches();

  const { isFailed: isUploadFailed } = useFlatfileUploadJobStatus(
    queryBulkBatch.data
  );

  let deleteModalProps = {
    modalDesc:
      "This batch has line items but no payables or deductions are created yet.",
    emptyBatchModalDesc:
      "This batch is empty. No payable or line items are imported yet.",
    redirect: goToPayableBatches
  };
  if (bulkResource === BulkResource.Collaborator) {
    deleteModalProps = {
      modalDesc:
        "This batch has contractors data but no contractors are created yet.",
      emptyBatchModalDesc:
        "This batch is empty. No contractors are imported yet.",
      redirect: goToContractorBatches
    };
  }
  const { modalDesc, emptyBatchModalDesc, redirect } = deleteModalProps;

  const deleteBulkImport = async () => {
    await deleteBulkBatch(
      { bulkBatchId },
      {
        onSuccess: (response: IBulkBatch) => {
          success(
            `${getUploadedFilename(
              response,
              bulkResource
            )} successfully deleted.`
          );
          onClose();
          redirect();
        },
        onError: err => {
          warning(
            `Failed to delete: ${
              err.response?.data?.error || "Something went wrong!"
            }`
          );
        }
      }
    );
  };
  return (
    <WSList gap="L">
      <WSText.ParagraphSm weight="book" color="gray600">
        Are you sure you want to delete this batch?
      </WSText.ParagraphSm>

      <WSInfoBox>{isEmptyBatch ? emptyBatchModalDesc : modalDesc}</WSInfoBox>

      {isUploadFailed && (
        <WSAlert
          theme="warning"
          icon="alert"
          name="deleteBatchWithErrors"
          title="Warning"
        >
          This import has errors.
          <br />
          Deleting it may complicate the investigation, as it permanently
          removes information that might be needed to determine the cause.
        </WSAlert>
      )}

      <WSActions
        alignment="fill"
        sortButtons={false}
        buttons={[
          {
            label: "Cancel",
            onClick: onClose,
            kind: "Tertiary"
          },
          {
            label: "Delete",
            onClick: deleteBulkImport,
            destructive: true,
            loading: deleteBulkBatchMeta.isLoading
          }
        ]}
      />
    </WSList>
  );
};

export const useModalConfirmBulkBatchDelete = () =>
  useWSModal(ModalConfirmBulkBatchDelete, {
    title: "Delete batch?",
    size: "S",
    showCloseIcon: false
  });
